<script lang="ts">
  import { onMount } from 'svelte'
  import { get } from 'svelte/store'
  import { settingsStore, settingsActions, type AIProvider } from '$lib/stores/settings'
  import { subscriptionStore } from '$lib/stores/subscription'
  import { useStorageListener } from '$lib/hooks/useEventListener'
  import { useDebounce, useTimeout } from '$lib/hooks/useTimer'
  import AIProviderCard from './AIProviderCard.svelte'
  import ProxyServiceNotice from './ProxyServiceNotice.svelte'
  import openrouterIcon from '../../assets/ai-model-icons/openrouter.png'
  
  let providers = $state<AIProvider[]>([])
  let saveMessage = $state('')
  let showApiKeys = $state<Record<string, boolean>>({})
  let showSaveIndicator = $state(false)
  
  // Proxy service status and usage
  let proxyUsage = $state({
    dailyRequests: 0,
    monthlyRequests: 0,
    dailyLimit: 100,
    monthlyLimit: 3000,
    tier: 'premium' as 'free' | 'premium'
  })
  
  let subscription = $state({
    type: 'free' as 'free' | 'trial' | 'premium',
    hasAIAnalysis: false
  })
  
  // Load settings from store
  function loadSettings() {
    const currentSettings = get(settingsStore)
    if (currentSettings.aiProviders) {
      // Filter out hidden providers (like OpenRouter)
      providers = [...currentSettings.aiProviders].filter(p => !p.hidden)
      
      // Load API keys from settings into provider objects
      for (const provider of providers) {
        if (provider.id === 'anthropic') {
          if (currentSettings.ai?.anthropicApiKey) {
            provider.apiKey = currentSettings.ai.anthropicApiKey
          }
        } else if (provider.id === 'openai') {
          if (currentSettings.ai?.openaiApiKey) {
            provider.apiKey = currentSettings.ai.openaiApiKey
          }
        } else if (provider.id === 'google') {
          if (currentSettings.ai?.googleApiKey) {
            provider.apiKey = currentSettings.ai.googleApiKey
          }
        } else if (provider.id === 'upstage') {
          if (currentSettings.ai?.upstageApiKey) {
            provider.apiKey = currentSettings.ai.upstageApiKey
          }
        }
      }
    }
  }
  
  // Initialize from settings on mount and subscribe to changes
  onMount(() => {
    loadSettings()
    
    // Subscribe to settings changes
    const unsubscribe = settingsStore.subscribe(() => {
      loadSettings()
    })
    
    // Subscribe to subscription updates
    const unsubscribeSub = subscriptionStore.subscribe($sub => {
      subscription = {
        type: $sub.type,
        hasAIAnalysis: $sub.features.aiAnalysis
      }
      
      // Update proxy usage based on subscription
      if ($sub.type === 'premium' || $sub.type === 'trial') {
        proxyUsage.tier = 'premium'
        proxyUsage.dailyLimit = $sub.dailyAIAnalysisLimit === -1 ? Infinity : $sub.dailyAIAnalysisLimit
        proxyUsage.monthlyLimit = proxyUsage.dailyLimit * 30
        proxyUsage.dailyRequests = $sub.currentDailyUsage.aiAnalyses
        proxyUsage.monthlyRequests = $sub.currentMonthlyUsage?.aiAnalyses || $sub.currentDailyUsage.aiAnalyses * 30 // Use actual monthly or estimate
      } else {
        proxyUsage.tier = 'free'
        proxyUsage.dailyLimit = $sub.dailyAIAnalysisLimit
        proxyUsage.monthlyLimit = $sub.dailyAIAnalysisLimit * 30
        proxyUsage.dailyRequests = $sub.currentDailyUsage.aiAnalyses
        proxyUsage.monthlyRequests = $sub.currentMonthlyUsage?.aiAnalyses || $sub.currentDailyUsage.aiAnalyses * 30 // Use actual monthly or estimate
      }
      
    })
    
    return () => {
      unsubscribe()
      unsubscribeSub()
    }
  })
  
  // Use storage listener hook for cross-tab sync
  useStorageListener('korean-grammar:settings', () => {
    loadSettings()
  })
  
  function toggleProvider(providerId: string) {
    const provider = providers.find(p => p.id === providerId)
    if (provider) {
      provider.enabled = !provider.enabled
      // Allow multiple providers to be enabled simultaneously
      // This allows users to have fallback options or switch between providers
    }
  }
  
  function updateApiKey(providerId: string, value: string) {
    const provider = providers.find(p => p.id === providerId)
    if (provider) {
      provider.apiKey = value
    }
  }
  
  function toggleApiKeyVisibility(providerId: string) {
    showApiKeys[providerId] = !showApiKeys[providerId]
  }
  
  
  // Timer for hiding save indicator
  const hideSaveIndicatorTimer = useTimeout(() => {
    showSaveIndicator = false
  }, 1000, false)
  
  // Debounced save function
  const debouncedSave = useDebounce(() => {
    saveSettingsInternal()
    // Hide save indicator after a short delay
    hideSaveIndicatorTimer.start()
  }, 500)
  
  function autoSaveSettings() {
    // Show save indicator
    showSaveIndicator = true
    
    // Debounce save
    debouncedSave()
  }
  
  function saveSettingsInternal() {
    // Get all providers including hidden ones
    const currentSettings = get(settingsStore)
    const hiddenProviders = currentSettings.aiProviders.filter(p => p.hidden)
    
    // Merge visible providers with hidden ones
    const allProviders = [...providers, ...hiddenProviders]
    
    // Update providers settings
    settingsActions.updateAIProviders(allProviders)
    
    // Update AI settings with API keys
    const apiKeyUpdates: any = {}
    
    // Find all enabled providers
    const enabledProviders = providers.filter(p => p.enabled)
    
    for (const provider of providers) {
      if (provider.apiKey) {
        // Map provider API keys to settings store format
        if (provider.id === 'anthropic') {
          apiKeyUpdates.anthropicApiKey = provider.apiKey
        } else if (provider.id === 'openai') {
          apiKeyUpdates.openaiApiKey = provider.apiKey
        } else if (provider.id === 'google') {
          apiKeyUpdates.googleApiKey = provider.apiKey
        } else if (provider.id === 'upstage') {
          apiKeyUpdates.upstageApiKey = provider.apiKey
        }
      }
    }
    
    // If we have enabled providers, use the first one as the primary provider
    // But keep all enabled for fallback options
    if (enabledProviders.length > 0) {
      const primaryProvider = enabledProviders[0]
      apiKeyUpdates.provider = primaryProvider.id
      // Use first available model from the provider
      if (primaryProvider.models && primaryProvider.models.length > 0) {
        apiKeyUpdates.selectedModel = primaryProvider.models[0]
      }
    }
    
    
    settingsActions.updateAISettings({
      temperature: 0.3,  // Use default temperature
      maxTokens: 2000,   // Will be auto-calculated based on input
      ...apiKeyUpdates
    })
    
    // Settings saved automatically
  }
</script>

<div class="ai-settings">
  <h2 class="section-title">AI 설정</h2>
  <p class="section-description">
    문법 검사 결과를 개선하기 위한 AI 제공자를 설정합니다.
  </p>
  
  <!-- Default AI Service -->
  <div class="proxy-service-section">
    <div class="proxy-header">
      <div class="proxy-title">
        <img src={openrouterIcon} alt="OpenRouter" class="openrouter-logo" />
        <h3>기본 AI</h3>
        <span class="premium-badge">프리미엄</span>
      </div>
      <span class="provider-text">OpenRouter</span>
    </div>
    
    <!-- Usage Information -->
    <div class="usage-section">
      <div class="usage-row">
        <span class="usage-label">일일 사용량</span>
        <div class="usage-info">
          <div class="usage-bar">
            <div 
              class="usage-fill" 
              style="width: {proxyUsage.dailyLimit === Infinity ? 0 : (proxyUsage.dailyRequests / proxyUsage.dailyLimit * 100)}%"
            ></div>
          </div>
          <span class="usage-text">
            {proxyUsage.dailyRequests} / {proxyUsage.dailyLimit === Infinity ? '무제한' : proxyUsage.dailyLimit}
          </span>
        </div>
      </div>
      
      <div class="usage-row">
        <span class="usage-label">월간 사용량</span>
        <div class="usage-info">
          <div class="usage-bar">
            <div 
              class="usage-fill" 
              style="width: {proxyUsage.monthlyLimit === Infinity ? 0 : (proxyUsage.monthlyRequests / proxyUsage.monthlyLimit * 100)}%"
            ></div>
          </div>
          <span class="usage-text">
            {proxyUsage.monthlyRequests} / {proxyUsage.monthlyLimit === Infinity ? '무제한' : proxyUsage.monthlyLimit.toLocaleString()}
          </span>
        </div>
      </div>
    </div>
    
    <div class="available-models">
      <span class="models-label">모델</span>
      <div class="model-chips">
        <span class="model-chip">GPT-OSS 120B</span>
        <span class="model-chip">Qwen3 32B</span>
      </div>
    </div>
  </div>
  
  <!-- Provider List -->
  <div class="providers-list">
    {#each providers as provider}
      <AIProviderCard
        {provider}
        showApiKey={showApiKeys[provider.id]}
        onToggle={() => {
          toggleProvider(provider.id)
          autoSaveSettings()
        }}
        onUpdateApiKey={(value) => {
          updateApiKey(provider.id, value)
          autoSaveSettings()
        }}
        onToggleApiKeyVisibility={() => toggleApiKeyVisibility(provider.id)}
      />
    {/each}
  </div>
  
  <!-- Auto-save indicator -->
  {#if showSaveIndicator}
    <div class="save-indicator">
      <span class="save-icon">✓</span>
      <span>저장됨</span>
    </div>
  {/if}
</div>

<style>
  /* Main container - Original styles preserved */
  .ai-settings {
    padding: 24px;
    max-width: 800px;
    margin: 0 auto;
  }
  
  .section-title {
    font-size: 18px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0 0 8px 0;
  }
  
  .section-description {
    font-size: 14px;
    color: var(--text-secondary);
    margin: 0 0 24px 0;
  }
  
  .providers-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 24px;
  }
  
  /* Proxy Service Section */
  .proxy-service-section {
    border: 1px solid var(--border-primary);
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 20px;
  }
  
  .proxy-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
  }
  
  .proxy-title {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .openrouter-logo {
    width: 18px;
    height: 18px;
    object-fit: contain;
    transition: filter 0.2s ease;
    opacity: 0.7;
  }
  
  /* Dark mode filter for logo */
  :global(.dark-theme) .openrouter-logo,
  :global([data-theme="dark"]) .openrouter-logo {
    filter: brightness(0) invert(1);
  }
  
  @media (prefers-color-scheme: dark) {
    :global(body:not(.light-theme)) .openrouter-logo {
      filter: brightness(0) invert(1);
    }
  }
  
  .proxy-title h3 {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
  }
  
  .premium-badge {
    padding: 2px 8px;
    background: linear-gradient(135deg, #667eea, #764ba2);
    color: white;
    border-radius: 10px;
    font-size: 10px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  .provider-text {
    font-size: 10px;
    color: var(--text-tertiary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  /* Usage Section */
  .usage-section {
    background: var(--bg-secondary);
    border-radius: 6px;
    padding: 10px;
    margin-bottom: 12px;
  }
  
  .usage-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 0;
  }
  
  .usage-row:not(:last-child) {
    border-bottom: 1px solid var(--border-primary);
  }
  
  .usage-label {
    font-size: 11px;
    color: var(--text-secondary);
    font-weight: 500;
  }
  
  .usage-info {
    display: flex;
    align-items: center;
    gap: 10px;
    flex: 1;
    max-width: 200px;
  }
  
  .usage-bar {
    flex: 1;
    height: 2px;
    background: var(--bg-tertiary);
    overflow: hidden;
  }
  
  .usage-fill {
    height: 100%;
    background: linear-gradient(90deg, var(--accent-primary), var(--accent-secondary));
    transition: width 0.3s ease;
  }
  
  .usage-text {
    font-size: 10px;
    color: var(--text-primary);
    font-weight: 500;
    white-space: nowrap;
  }
  
  /* Available Models */
  .available-models {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  
  .models-label {
    font-size: 11px;
    color: var(--text-tertiary);
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  .model-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }
  
  .model-chip {
    padding: 3px 8px;
    background: var(--bg-tertiary);
    border-radius: 10px;
    font-size: 10px;
    font-weight: 500;
    color: var(--text-secondary);
  }
  
  /* Save Indicator */
  .save-indicator {
    position: fixed;
    bottom: 20px;
    right: 20px;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    background: var(--success);
    color: white;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 500;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    animation: slideIn 0.3s ease;
    z-index: 1000;
  }
  
  .save-icon {
    font-size: 14px;
    font-weight: bold;
  }
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  /* Dark mode handled by CSS variables */
</style>